import axios from "axios";
import { ChimeSDKMessagingClient } from "@aws-sdk/client-chime-sdk-messaging";
import { CLASSROOM_S3_PATH } from "../../utils/constants";
import { getDecryptedDataWithSecretKey } from "../../utils/helper";

let cachedClient: ChimeSDKMessagingClient | null = null;
let cachedAppInstanceArn: string | null = null;
let clientPromise: Promise<{ messagingClient: ChimeSDKMessagingClient; appInstanceArn: string }> | null = null;

export const getChimeMessagingClient = async () => {
  if (cachedClient && cachedAppInstanceArn) {
    return {
      messagingClient: cachedClient,
      appInstanceArn: cachedAppInstanceArn,
    };
  }

  if (clientPromise) {
    return clientPromise;
  }

  clientPromise = (async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    if (!backendUrl) {
      throw new Error("Backend URL is not defined in environment variables");
    }

    const query = `
      query getRoleKeysInfo($type: String!) {
        getRoleKeysInfo(type: $type) {
          accessKeyId
          secretAccessKey
          sessionToken
          clientRegion
          appInstanceArn
        }
      }
    `;

    const { data } = await axios.post(
      backendUrl,
      {
        query,
        variables: { type: CLASSROOM_S3_PATH },
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    const { accessKeyId, secretAccessKey, sessionToken, clientRegion, appInstanceArn } =
      data?.data?.getRoleKeysInfo ?? {};

    if (!accessKeyId || !secretAccessKey || !sessionToken || !clientRegion) {
      throw new Error("Missing necessary credentials for Chime SDK");
    }

    const decryptedAccessKeyId = getDecryptedDataWithSecretKey(accessKeyId).originalText;
    const decryptedSecretAccessKey = getDecryptedDataWithSecretKey(secretAccessKey).originalText;
    const decryptedSessionToken = getDecryptedDataWithSecretKey(sessionToken).originalText;

    const messagingClient = new ChimeSDKMessagingClient({
      region: clientRegion,
      credentials: {
        accessKeyId: decryptedAccessKeyId,
        secretAccessKey: decryptedSecretAccessKey,
        sessionToken: decryptedSessionToken,
      },
    });

    cachedClient = messagingClient;
    cachedAppInstanceArn = appInstanceArn;

    return {
      messagingClient,
      appInstanceArn,
    };
  })();

  return clientPromise;
};

export function clearChimeMessagingClientCache() {
  cachedClient = null;
  cachedAppInstanceArn = null;
  clientPromise = null;
}
